import { NavLink, useLocation } from 'react-router-dom'
import { createPortal } from 'react-dom'
import {
  ChevronLeft,
  ChevronRight,
  FolderOpen,
  Home,
  Loader2,
  PanelLeftClose,
  SidebarOpen,
} from 'lucide-react'
import { useMenu } from '../../hooksCompartilhados/useMenu'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { MenuItemDto } from '../../tiposGlobais/menu'
import type { SidebarNavMode } from '../../hooksCompartilhados/useSidebarNavMode'
import { useUsuario } from '../../contexts/UsuarioContext'
import { getIconComponent } from '../form/icon-picker/iconLibraries'
import { coletarAncestraisMenuPorRota } from './menuAncestorUtils'

const quickStats = [
  { label: 'Projetos Ativos', value: '12', color: 'text-brand-400' },
  { label: 'Pendências', value: '3', color: 'text-warning-400' },
  { label: 'Concluídos', value: '45', color: 'text-success-400' },
]

/** Persiste interação do rail ao recarregar a página (F5); rotas ficam sob um único AppLayout para não perder estado na navegação. */
const SESSION_RAIL_INTERACAO_SUBMENU = 'zeus.sidebar.usuarioInteracaoRailSubmenu'

function lerRailInteracaoPersistida(): boolean {
  if (typeof window === 'undefined')
    return false
  return sessionStorage.getItem(SESSION_RAIL_INTERACAO_SUBMENU) === '1'
}

function persistirRailInteracao(usuarioInteragiu: boolean) {
  if (typeof window === 'undefined')
    return
  if (usuarioInteragiu)
    sessionStorage.setItem(SESSION_RAIL_INTERACAO_SUBMENU, '1')
  else
    sessionStorage.removeItem(SESSION_RAIL_INTERACAO_SUBMENU)
}

export interface AppSidebarProps {
  isOpen: boolean
  onClose: () => void
  navMode: SidebarNavMode
  onNavModeChange: (mode: SidebarNavMode) => void
  /** Preferência: em desktop, em rotas filhas, ocultar menu principal e usar só o rail do grupo. */
  somenteRailSubmenu: boolean
  onSomenteRailSubmenuChange: (ativo: boolean) => void
  /** `true` quando a preferência está ligada, viewport ≥ lg e há ancestral no menu. */
  railAtivoDesktop: boolean
  /** Mantém a margem da coluna principal (`lg:ml-*`) alinhada à largura visual da sidebar. */
  onLgMainMarginClass?: (marginClass: string) => void
}

function excludeItem(item: MenuItemDto, isAdmin: boolean, isAnalista: boolean): boolean {
  if (!isAdmin && item.rota?.startsWith('/times'))
    return true
  if (!isAdmin && item.rota && (item.rota.includes('/faturamento/upload-planilha-suporte') || item.rota.includes('/faturamento/lancamento-manual')))
    return true
  if (item.rota?.includes('/suportes/geracao-envio') && !isAnalista)
    return true
  return false
}

function visibleChildren(item: MenuItemDto, isAdmin: boolean, isAnalista: boolean): MenuItemDto[] {
  return (item.filhos ?? []).filter((child) => {
    if (!isAdmin && child.rota?.startsWith('/times'))
      return false
    if (!isAdmin && child.rota && (child.rota.includes('/faturamento/upload-planilha-suporte') || child.rota.includes('/faturamento/lancamento-manual')))
      return false
    if (child.rota?.includes('/suportes/geracao-envio') && !isAnalista)
      return false
    return true
  })
}

function submenuTemRotaOuFilhos(items: MenuItemDto[], isAdmin: boolean, isAnalista: boolean): boolean {
  for (const item of items)
  {
    if (excludeItem(item, isAdmin, isAnalista))
      continue
    const ch = visibleChildren(item, isAdmin, isAnalista)
    if (item.rota?.trim())
      return true
    if (ch.length > 0 && submenuTemRotaOuFilhos(ch, isAdmin, isAnalista))
      return true
  }
  return false
}

interface FlyoutItemProps {
  item: MenuItemDto
  isAdmin: boolean
  isAnalista: boolean
  pathname: string
  depth: number
  onNavigate: () => void
}

function FlyoutListaItem(props: FlyoutItemProps): React.ReactElement | null {
  const IconComponent = getIconComponent(props.item.icone || '') || Home
  const children = visibleChildren(props.item, props.isAdmin, props.isAnalista)
  const excluded = excludeItem(props.item, props.isAdmin, props.isAnalista)

  if (excluded)
    return null

  const hasRenderableChildren =
    children.length > 0 && submenuTemRotaOuFilhos(children, props.isAdmin, props.isAnalista)

  if (!hasRenderableChildren && props.item.filhos && props.item.filhos.length > 0)
    return null

  const padLeft = Math.min(8 + props.depth * 8, 32)

  if (children.length > 0 && hasRenderableChildren)
  {
    return (
    <div className="space-y-1">
      <div
        style={{ paddingLeft: padLeft }}
        className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500"
      >
        {props.item.titulo}
      </div>
      <div className="space-y-0.5 border-l border-gray-700/80 pl-2">
        {children.map((ch) => (
          <FlyoutListaItem key={ch.codigo} {...props} item={ch} depth={props.depth + 1} />
        ))}
      </div>
    </div>
    )
  }

  if (children.length > 0 && !hasRenderableChildren)
    return null

  if (!props.item.rota?.trim())
    return null
  const isActive = props.pathname === props.item.rota
  return (
    <NavLink
      to={props.item.rota}
      onClick={props.onNavigate}
      style={{ paddingLeft: padLeft }}
      className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-gray-700 hover:text-white ${
        isActive ? 'bg-gray-700 text-white' : 'text-gray-300'
      }`}
    >
      <IconComponent className="h-4 w-4 shrink-0 opacity-90" />
      <span>{props.item.titulo}</span>
    </NavLink>
  )
}

export function AppSidebar({
  isOpen,
  onClose,
  navMode,
  onNavModeChange,
  somenteRailSubmenu,
  onSomenteRailSubmenuChange,
  railAtivoDesktop,
  onLgMainMarginClass,
}: AppSidebarProps) {
  const { menuItems, loading, error } = useMenu()
  const location = useLocation()
  const { usuario } = useUsuario()
  const isAdmin = usuario?.papeis?.includes('1') ?? false
  const isAnalista = usuario?.papeis?.includes('2') ?? false

  const modoIcones = navMode === 'icons'

  const [flyoutCodigo, setFlyoutCodigo] = useState<number | null>(null)
  const [flyoutCss, setFlyoutCss] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const gatilhosRef = useRef<Record<number, HTMLButtonElement | null>>({})
  const painelRef = useRef<HTMLDivElement>(null)
  const flyoutCodigoRef = useRef<number | null>(null)

  const ancestrais = useMemo(() => coletarAncestraisMenuPorRota(menuItems, location.pathname) ?? [], [menuItems, location.pathname])

  const codigoGrupoRaiz = ancestrais.length > 0 ? ancestrais[0] : undefined
  /** Qual grupo raíz mostrar no rail quando difere da URL (ex.: rota é Faturamento mas o usuário abriu Projetos). Zerado ao mudar pathname. */
  const [railGrupoTopoPreferidoCodigo, setRailGrupoTopoPreferidoCodigo] = useState<number | undefined>(undefined)

  useEffect(() => {
    setRailGrupoTopoPreferidoCodigo(undefined)
  }, [location.pathname])

  const codigoTopoParaGrupoRail = railGrupoTopoPreferidoCodigo ?? codigoGrupoRaiz
  const grupoItemMenu = useMemo(
    () => (codigoTopoParaGrupoRail != null ? menuItems.find((i) => i.codigo === codigoTopoParaGrupoRail) : undefined),
    [menuItems, codigoTopoParaGrupoRail],
  )

  const [menuPrincipalExpandidoTemp, setMenuPrincipalExpandidoTemp] = useState(false)
  /** Com a opção de colapsar ligada, o menu principal só some depois de abrir o flyout ou clicar num link da árvore (não ao só entrar na rota filha). */
  const [usuarioAcionouArvoreSubmenu, setUsuarioAcionouArvoreSubmenu] = useState(lerRailInteracaoPersistida)

  useEffect(() => {
    persistirRailInteracao(usuarioAcionouArvoreSubmenu)
  }, [usuarioAcionouArvoreSubmenu])

  useEffect(() => {
    if (!railAtivoDesktop)
      setMenuPrincipalExpandidoTemp(false)
  }, [railAtivoDesktop])

  useEffect(() => {
    if (!somenteRailSubmenu)
      setUsuarioAcionouArvoreSubmenu(false)
  }, [somenteRailSubmenu])

  /** Troca de seção (Faturamento → Projetos, etc.) não zera mais o modo "só submenu"; só reafirma se o flyout estiver aberto. */
  useEffect(() => {
    if (somenteRailSubmenu && flyoutCodigo !== null)
      setUsuarioAcionouArvoreSubmenu(true)
  }, [flyoutCodigo, somenteRailSubmenu])

  const mostrarApenasRailGrupo =
    railAtivoDesktop &&
    usuarioAcionouArvoreSubmenu &&
    !menuPrincipalExpandidoTemp &&
    grupoItemMenu != null &&
    !loading &&
    !error

  useLayoutEffect(() => {
    const colunaAlinhadaRailOuExpandida = mostrarApenasRailGrupo || navMode === 'full'
    onLgMainMarginClass?.(colunaAlinhadaRailOuExpandida ? 'lg:ml-72' : 'lg:ml-[4.5rem]')
  }, [mostrarApenasRailGrupo, navMode, onLgMainMarginClass])

  flyoutCodigoRef.current = flyoutCodigo

  useEffect(() => setFlyoutCodigo(null), [location.pathname])

  const reposicionarPainel = useCallback(() => {
    if (typeof window === 'undefined')
      return
    const codigoAtual = flyoutCodigoRef.current
    if (codigoAtual === null)
      return
    const btn = gatilhosRef.current[codigoAtual]
    if (!btn)
      return
    const rect = btn.getBoundingClientRect()
    const vw = window.innerWidth
    const pad = 8
    const painelWide = Math.min(Math.min(vw * 0.92, 320), vw - pad * 2)
    let left = rect.right + pad
    if (left + painelWide > vw - pad)
      left = Math.max(pad, rect.left - pad - painelWide)

    let top = rect.top
    const altoMaximo = vw > 768 ? Math.min(window.innerHeight * 0.75, 520) : window.innerHeight * 0.85
    if (top + altoMaximo > window.innerHeight - pad)
      top = Math.max(pad, window.innerHeight - altoMaximo - pad)

    setFlyoutCss((curr) =>
    {
      if (curr.left === left && curr.top === top)
        return curr
      return { left, top }
    })
  }, [])

  useLayoutEffect(() => {
    reposicionarPainel()
  }, [reposicionarPainel, flyoutCodigo, navMode])

  useEffect(() => {
    if (typeof window === 'undefined')
      return undefined
    window.addEventListener('resize', reposicionarPainel)
    window.addEventListener('scroll', reposicionarPainel, true)
    return () => {
      window.removeEventListener('resize', reposicionarPainel)
      window.removeEventListener('scroll', reposicionarPainel, true)
    }
  }, [reposicionarPainel])

  useEffect(() => {
    if (flyoutCodigo === null)
      return undefined
    const fechaFora = (e: MouseEvent) => {
      const t = e.target as Node
      if (!t)
        return
      if (painelRef.current?.contains(t))
        return
      const g = gatilhosRef.current[flyoutCodigo]
      if (g?.contains(t))
        return
      setFlyoutCodigo(null)
    }
    document.addEventListener('mousedown', fechaFora)
    const aoEsc = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape')
        setFlyoutCodigo(null)
    }
    window.addEventListener('keydown', aoEsc)
    return () => {
      document.removeEventListener('mousedown', fechaFora)
      window.removeEventListener('keydown', aoEsc)
    }
  }, [flyoutCodigo])

  const quandoGatilho = (codigo: number) => {
    setFlyoutCodigo((v) => {
      const next = v === codigo ? null : codigo
      if (next !== null)
      {
        const ehItemRaiz = menuItems.some((raiz) => raiz.codigo === next)
        queueMicrotask(() => {
          if (somenteRailSubmenu)
          {
            setUsuarioAcionouArvoreSubmenu(true)
            setMenuPrincipalExpandidoTemp(false)
          }
          if (ehItemRaiz)
            setRailGrupoTopoPreferidoCodigo(next)
        })
      }
      return next
    })
  }

  const aoIr = () => {
    if (somenteRailSubmenu)
    {
      setUsuarioAcionouArvoreSubmenu(true)
      setMenuPrincipalExpandidoTemp(false)
    }
    setFlyoutCodigo(null)
    onClose()
  }

  const renderLinhaTopo = (item: MenuItemDto): React.ReactElement | null => {
    const IconComponent = getIconComponent(item.icone || '') || Home

    if (excludeItem(item, isAdmin, isAnalista))
      return null

    const filhosVer = visibleChildren(item, isAdmin, isAnalista)
    const ramificacao =
      filhosVer.length > 0 && submenuTemRotaOuFilhos(filhosVer, isAdmin, isAnalista)

    if (!ramificacao && item.filhos && item.filhos.length > 0)
      return null

    const flyoutsEste = flyoutCodigo === item.codigo
    const topoEhAncestral = ramificacao && ancestrais.length > 0 && ancestrais[0] === item.codigo

    const padIcoes = modoIcones ? 'px-2' : 'px-4'

    if (!ramificacao)
    {
      if (!item.rota?.trim())
        return null
      const leafAtiva = location.pathname === item.rota
      return (
        <li key={item.codigo}>
          <NavLink
            to={item.rota}
            title={modoIcones ? item.titulo : undefined}
            onClick={onClose}
            className={`group flex w-full items-center rounded-sm py-2.5 font-medium transition-colors duration-200 hover:bg-gray-700 hover:text-white ${padIcoes} ${
              modoIcones ? 'justify-center' : 'gap-2.5 justify-start'
            } ${leafAtiva ? 'bg-gray-700 text-white' : 'text-gray-300'}`}
          >
            <IconComponent className="h-5 w-5 shrink-0 opacity-95" />
            {!modoIcones ? <span className="min-w-0 truncate">{item.titulo}</span> : null}
          </NavLink>
        </li>
      )
    }

    return (
      <li key={item.codigo} className="relative">
        <button
          ref={(el) => {
            gatilhosRef.current[item.codigo] = el
          }}
          type="button"
          title={modoIcones ? item.titulo : undefined}
          onClick={() => quandoGatilho(item.codigo)}
          className={`group flex w-full items-center rounded-sm py-2.5 font-medium transition-colors duration-200 hover:bg-gray-700 hover:text-white ${padIcoes} ${
            modoIcones ? 'justify-center' : 'justify-between gap-2'
          } ${
            flyoutsEste
              ? 'bg-gray-700 text-white'
              : topoEhAncestral
              ? 'bg-gray-800/90 text-gray-50'
              : 'text-gray-300'
          }`}
          aria-expanded={flyoutsEste}
          aria-haspopup="true"
        >
          <span className={`flex min-w-0 items-center gap-2 ${modoIcones ? 'justify-center' : ''}`}>
            <IconComponent className="h-5 w-5 shrink-0 opacity-95" />
            {!modoIcones ? <span className="truncate">{item.titulo}</span> : null}
          </span>
          {!modoIcones ? (
            <ChevronRight
              className={`h-4 w-4 shrink-0 transition-transform duration-150 ${flyoutsEste ? 'rotate-90 text-white' : 'text-gray-500'}`}
            />
          ) : flyoutsEste ? (
            <span className="absolute right-1.5 top-1/2 hidden h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-brand-400 lg:inline" />
          ) : null}
        </button>
      </li>
    )
  }

  const itemPainel = menuItems.find((i) => i.codigo === flyoutCodigo)
  const podeMostrarPainel =
    flyoutCodigo !== null &&
    itemPainel !== undefined &&
    visibleChildren(itemPainel, isAdmin, isAnalista).length > 0

  const conteudoPainel =
    podeMostrarPainel && itemPainel
      ? (
        <>
          <div className="border-b border-gray-700/90 px-3 py-2.5 text-sm font-semibold tracking-tight text-white">
            {itemPainel.titulo}
          </div>
          <div className="no-scrollbar max-h-[min(72vh,_520px)] overflow-y-auto px-3 py-3">
            <div className="flex flex-col gap-1">
              {visibleChildren(itemPainel, isAdmin, isAnalista).map((ch) => (
                <FlyoutListaItem
                  key={ch.codigo}
                  item={ch}
                  isAdmin={isAdmin}
                  isAnalista={isAnalista}
                  pathname={location.pathname}
                  depth={0}
                  onNavigate={aoIr}
                />
              ))}
            </div>
          </div>
        </>
      )
      : null

  const aoIrRail = () => {
    if (somenteRailSubmenu)
    {
      setUsuarioAcionouArvoreSubmenu(true)
      setMenuPrincipalExpandidoTemp(false)
    }
    setFlyoutCodigo(null)
    onClose()
  }

  const IconeGrupo =
    grupoItemMenu != null
      ? getIconComponent(grupoItemMenu.icone || '') || Home
      : Home

  const asideRail =
    grupoItemMenu != null && mostrarApenasRailGrupo
      ? (
        <aside className="fixed left-0 top-0 z-50 flex h-[100dvh] min-h-[100dvh] w-72 flex-col overflow-hidden border-r border-gray-800/90 bg-gray-900 lg:translate-x-0">
          <div className="flex shrink-0 flex-col gap-3 border-b border-gray-800/80 px-4 py-4">
            <button
              type="button"
              className="inline-flex items-center gap-2 text-left text-sm font-semibold text-brand-400 transition-colors hover:text-brand-300"
              onClick={() => setMenuPrincipalExpandidoTemp(true)}
            >
              <ChevronLeft className="h-4 w-4 shrink-0" aria-hidden />
              Menu principal
            </button>
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-800/90 ring-1 ring-gray-700/80">
                <IconeGrupo className="h-5 w-5 text-brand-400" aria-hidden />
              </div>
              <span className="min-w-0 truncate text-lg font-bold leading-tight text-white">{grupoItemMenu.titulo}</span>
            </div>
          </div>
          <div className="no-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto px-3 py-4">
            <div className="flex flex-col gap-1">
              {visibleChildren(grupoItemMenu, isAdmin, isAnalista).map((ch) => (
                <FlyoutListaItem
                  key={ch.codigo}
                  item={ch}
                  isAdmin={isAdmin}
                  isAnalista={isAnalista}
                  pathname={location.pathname}
                  depth={0}
                  onNavigate={aoIrRail}
                />
              ))}
            </div>
          </div>
          <div className="shrink-0 border-t border-gray-800 bg-gray-950/95 px-3 py-3 backdrop-blur-sm lg:pb-6">
            <label className="flex cursor-pointer flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-xs font-medium text-gray-400">Só submenu nesta rota</span>
              <span className="relative inline-flex shrink-0 items-center">
                <input
                  type="checkbox"
                  checked={somenteRailSubmenu}
                  onChange={(e) => onSomenteRailSubmenuChange(e.target.checked)}
                  className="peer sr-only"
                />
                <span className="h-7 w-[3rem] shrink-0 rounded-full border border-gray-600 bg-gray-800 transition peer-checked:bg-brand-600 peer-checked:border-brand-500 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand-400" />
                <span className="pointer-events-none absolute left-1 top-1 flex h-5 w-5 translate-x-0 rounded-full bg-white shadow transition-transform duration-150 peer-checked:translate-x-6 peer-checked:bg-white" />
              </span>
            </label>
            <p className="mt-2 text-[10px] leading-snug text-gray-500">
              Desligue para voltar ao menu completo até você abrir o submenu outra vez.
            </p>
          </div>
        </aside>
      )
      : null

  return (
    <>
      {mostrarApenasRailGrupo
        ? asideRail
        : (
      <aside
        className={`fixed left-0 top-0 z-50 flex h-[100dvh] min-h-[100dvh] flex-col overflow-x-visible overflow-y-hidden border-r border-gray-800/90 bg-gray-900 transition-[width,transform] duration-300 ease-out lg:translate-x-0 ${modoIcones ? 'w-72 lg:w-[4.5rem]' : 'w-72'} ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div
          className={`flex shrink-0 flex-wrap items-center gap-3 border-b border-gray-800/80 py-4 ${modoIcones ? 'justify-center lg:justify-center lg:gap-2 lg:px-2' : 'px-5 lg:justify-between lg:px-6'}`}
        >
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-r from-brand-500 to-brand-600 shadow-theme-sm ring-2 ring-brand-700/40">
              <FolderOpen className="h-5 w-5 text-white" />
            </div>
            {!modoIcones ? (
              <div className="min-w-0">
                <p className="text-lg font-bold leading-tight text-white">Zeus</p>
                <p className="text-[11px] text-gray-400">Project Management</p>
              </div>
            ) : null}
          </div>
        </div>

        <div className="no-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-visible">
          <nav className="flex flex-col px-2 py-3 lg:py-6">
            {railAtivoDesktop && menuPrincipalExpandidoTemp ? (
              <div
                className={`mb-4 rounded-lg border border-brand-500/30 bg-brand-950/30 ${modoIcones ? 'flex justify-center py-2' : 'px-3 py-2'}`}
              >
                <button
                  type="button"
                  aria-label="Voltar ao só submenu"
                  title="Voltar ao só submenu"
                  className={
                    modoIcones
                      ? 'flex h-10 w-full max-w-[2.75rem] items-center justify-center rounded-md text-brand-300 transition-colors hover:bg-brand-900/60 hover:text-brand-200'
                      : 'text-xs font-semibold text-brand-300 transition-colors hover:text-brand-200'
                  }
                  onClick={() => setMenuPrincipalExpandidoTemp(false)}
                >
                  {modoIcones ? <PanelLeftClose className="h-5 w-5 shrink-0" aria-hidden /> : 'Voltar ao só submenu'}
                </button>
              </div>
            ) : null}
            <div className="mb-6 flex-shrink-0 lg:mb-10">
              <h3
                className={`mb-4 text-[11px] font-semibold uppercase tracking-wider text-gray-500 lg:mx-4 ${modoIcones ? 'mx-4 hidden lg:inline-block lg:w-0 lg:overflow-hidden lg:opacity-0' : ''}`}
              >
                MENU
              </h3>
              {loading
                ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-brand-400" />
                    {!modoIcones ? <span className="ml-2 text-sm text-gray-400">Carregando...</span> : null}
                  </div>
                )
                : error
                  ? (
                    <div className="rounded-md px-4 py-6 text-center">
                      <p className="text-sm text-red-400">Erro ao carregar menu</p>
                      <p className="mt-1 text-xs text-gray-500">{error}</p>
                    </div>
                  )
                  : (
                    <ul className="flex flex-col gap-px">{menuItems.map((item) => renderLinhaTopo(item))}</ul>
                  )}
            </div>

            {!modoIcones ? (
              <div className="flex-shrink-0 border-t border-gray-800 pb-4 pt-4">
                <h3 className="mb-3 ml-4 text-xs font-semibold uppercase tracking-wide text-gray-400">Resumo rápido</h3>
                <ul className="flex flex-col gap-px">
                  {quickStats.map((stat) => (
                    <li
                      key={stat.label}
                      className="flex items-center justify-between rounded-md px-4 py-2 text-sm text-gray-300 hover:bg-gray-700/70"
                    >
                      <span>{stat.label}</span>
                      <span className={`font-bold tabular-nums ${stat.color}`}>{stat.value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </nav>

          <div className="mt-auto shrink-0 border-t border-gray-800 bg-gray-950/95 px-3 py-3 backdrop-blur-sm lg:pb-6">
            {modoIcones ? (
              <button
                type="button"
                aria-label="Menu completo"
                title="Menu completo"
                className="flex w-full items-center justify-center rounded-lg border border-gray-700 bg-gray-800/70 py-2.5 text-brand-400 transition-colors hover:bg-gray-800 hover:text-brand-300 lg:aspect-square lg:max-h-11 lg:max-w-11 lg:p-0"
                onClick={() => onNavModeChange('full')}
              >
                <SidebarOpen className="h-5 w-5 shrink-0 text-brand-400" aria-hidden />
              </button>
            ) : (
              <label className="flex cursor-pointer flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-xs font-medium text-gray-400">Mostrar texto do menu</span>
                <span className="relative inline-flex shrink-0 items-center">
                  <input
                    type="checkbox"
                    checked={navMode === 'full'}
                    onChange={(e) => onNavModeChange(e.target.checked ? 'full' : 'icons')}
                    className="peer sr-only"
                  />
                  <span className="h-7 w-[3rem] shrink-0 rounded-full border border-gray-600 bg-gray-800 transition peer-checked:bg-brand-600 peer-checked:border-brand-500 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand-400" />
                  <span className="pointer-events-none absolute left-1 top-1 flex h-5 w-5 translate-x-0 rounded-full bg-white shadow transition-transform duration-150 peer-checked:translate-x-6 peer-checked:bg-white" />
                </span>
              </label>
            )}
            {!modoIcones ? (
              <label className="mt-4 flex cursor-pointer flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-xs font-medium text-gray-400">Só submenu em rotas filhas</span>
                <span className="relative inline-flex shrink-0 items-center">
                  <input
                    type="checkbox"
                    checked={somenteRailSubmenu}
                    onChange={(e) => onSomenteRailSubmenuChange(e.target.checked)}
                    className="peer sr-only"
                  />
                  <span className="h-7 w-[3rem] shrink-0 rounded-full border border-gray-600 bg-gray-800 transition peer-checked:bg-brand-600 peer-checked:border-brand-500 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand-400" />
                  <span className="pointer-events-none absolute left-1 top-1 flex h-5 w-5 translate-x-0 rounded-full bg-white shadow transition-transform duration-150 peer-checked:translate-x-6 peer-checked:bg-white" />
                </span>
              </label>
            ) : null}
            {!modoIcones ? (
              <p className="mt-2 text-[10px] leading-snug text-gray-500">
                Desligue o primeiro interruptor para só ícones e submenu ao lado. Com o segundo ligado, no desktop o menu principal some só depois que você abrir o submenu (flyout ou link da árvore); ao mudar de seção no menu, volta a pedir essa interação.
              </p>
            ) : null}
          </div>
        </div>
      </aside>
        )
      }

      {!mostrarApenasRailGrupo && podeMostrarPainel && conteudoPainel
        ? createPortal(
          <div
            ref={painelRef}
            className="fixed z-[60] flex w-[min(20rem,calc(100vw-16px))] flex-col overflow-hidden rounded-xl border border-gray-700 bg-gray-900 text-left shadow-theme-xl ring-1 ring-black/30"
            style={{ top: flyoutCss.top, left: flyoutCss.left }}
          >
            {conteudoPainel}
          </div>,
          document.body,
        )
      : null}
    </>
  )
}
